/*
 * dd-storefront-cart.js — Storefront API client for cart operations
 * the AJAX cart API doesn't expose (currently: gift card add/remove).
 *
 * Reads config from `window.__ddSF` (set in layout/theme.liquid):
 *   { token, domain, version, enabled, cartGid }
 *
 * Public API (window.ddSFCart):
 *   apply(code)              -> Promise<{ ok, applied?, all?, cost?, message?, raw? }>
 *   remove(applicationId)    -> Promise<{ ok, all?, cost?, message?, raw? }>
 *   query()                  -> Promise<{ appliedGiftCards, cost, raw } | null>
 *   isReady()                -> bool
 *
 * Why this exists:
 *   Shopify's AJAX cart endpoints (/cart/update.js, /cart/add.js) do NOT
 *   support gift card application. The Storefront GraphQL API does, via
 *   cartGiftCardCodesAdd. Since API 2023-04 the underlying cart resource
 *   is unified across both APIs — a mutation through the Storefront API
 *   on the cart whose id is `gid://shopify/Cart/{{ cart.token }}` is
 *   visible in the AJAX cart on the next /cart.js read and carries
 *   through to /checkout.
 *
 *   Since June 2024, /cart.js returns the token in the form
 *   `<token>?key=<key>`. The `?key=` portion is REQUIRED by the
 *   Storefront API for any mutation; querying without it returns a
 *   redacted cart, mutating returns "The specified cart does not
 *   exist." We pass `{{ cart.token }}` through verbatim — Liquid
 *   exposes the full token-with-key.
 *
 * Error detection:
 *   `cartGiftCardCodesAdd` is intentionally vague about invalid codes
 *   (anti-enumeration hardening): bogus codes return userErrors=[] AND
 *   leave appliedGiftCards unchanged. We detect rejection by comparing
 *   the appliedGiftCards count before vs. after the mutation.
 */
(function () {
  'use strict';

  if (window.ddSFCart) return;

  function cfg() {
    return window.__ddSF || {};
  }

  function isConfigured() {
    var c = cfg();
    return !!(c.enabled && c.token && c.domain && c.version);
  }

  function hasCart() {
    return !!(cfg().cartGid);
  }

  function isReady() {
    return isConfigured() && hasCart();
  }

  function readinessReason() {
    var c = cfg();
    if (!c.enabled) return 'kill switch off (settings.dd_enable_storefront_giftcards or token blank)';
    if (!c.token) return 'storefront token not set in theme settings';
    if (!c.domain) return 'shop domain missing';
    if (!c.version) return 'storefront API version missing';
    if (!c.cartGid) return 'cartGid not yet resolved (call refresh() or apply()/remove() to populate)';
    return 'ready';
  }

  /*
   * The Liquid-rendered `cartGid` in `window.__ddSF` is a snapshot from
   * page-render time. If the user added items via AJAX after page load
   * (the common case for a cart drawer flow), Liquid's snapshot is
   * stale. `refresh()` fetches the current cart token from /cart.js
   * and rebuilds the GID. Cost: 1 request to a same-origin Liquid
   * endpoint (no Cloudflare cross-origin penalty).
   *
   * `apply()` and `remove()` auto-refresh when cartGid is missing so
   * standalone console use works without an explicit refresh call.
   * In the wired-in dd-discount-code.js flow the caller can pass
   * { cartToken } in options to bypass the refresh and avoid the
   * extra request — the AJAX cart response from /cart/update.js
   * already carries the fresh token.
   */
  function refresh(opts) {
    var c = cfg();
    if (!c.domain) return Promise.resolve(null);
    if (opts && typeof opts.cartToken === 'string' && opts.cartToken.length) {
      c.cartGid = 'gid://shopify/Cart/' + opts.cartToken;
      return Promise.resolve(c.cartGid);
    }
    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (cart && cart.token) {
          c.cartGid = 'gid://shopify/Cart/' + cart.token;
          return c.cartGid;
        }
        c.cartGid = null;
        return null;
      })
      .catch(function () {
        return c.cartGid;
      });
  }

  function endpoint() {
    var c = cfg();
    return 'https://' + c.domain + '/api/' + c.version + '/graphql.json';
  }

  function gql(query, variables) {
    if (!isReady()) {
      return Promise.reject(new Error('ddSFCart: not ready — ' + readinessReason()));
    }
    return fetch(endpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg().token,
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    })
      .then(function (r) {
        if (!r.ok) {
          return r.text().then(function (t) {
            var err = new Error('Storefront API HTTP ' + r.status);
            err.status = r.status;
            err.body = t;
            throw err;
          });
        }
        return r.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) {
          var err = new Error(json.errors[0].message || 'Storefront GraphQL error');
          err.graphqlErrors = json.errors;
          throw err;
        }
        return json.data;
      });
  }

  var GQL_CART = [
    'query DDCart($id: ID!) {',
    '  cart(id: $id) {',
    '    id',
    '    appliedGiftCards {',
    '      id',
    '      lastCharacters',
    '      amountUsed { amount currencyCode }',
    '      balance { amount currencyCode }',
    '    }',
    '    cost {',
    '      totalAmount { amount currencyCode }',
    '      subtotalAmount { amount currencyCode }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  /* Gift card add — `warnings` is the field that reveals
     silent-reject reasons that Shopify deliberately omits from
     `userErrors` (anti-enumeration hardening for some codes,
     payments-availability checks for others). The most useful values
     we've seen in production are:
       • PAYMENTS_GIFT_CARDS_UNAVAILABLE — gift cards are configured
         as a payment method that isn't enabled on this store's
         Storefront API surface (checkout still has them, the cart
         layer doesn't). Route to the legacy "apply at checkout"
         handoff and stop trying the SF API path.
       • DISCOUNT_NOT_FOUND — the code doesn't match any active
         gift card. Surface a "code not recognized" message.
       • DISCOUNT_USAGE_LIMIT_REACHED / EXPIRED variants — surface
         specific message instead of the generic handoff. */
  var GQL_GC_ADD = [
    'mutation DDAddGiftCard($cartId: ID!, $codes: [String!]!) {',
    '  cartGiftCardCodesAdd(cartId: $cartId, giftCardCodes: $codes) {',
    '    cart {',
    '      id',
    '      appliedGiftCards {',
    '        id',
    '        lastCharacters',
    '        amountUsed { amount currencyCode }',
    '        balance { amount currencyCode }',
    '      }',
    '      cost {',
    '        totalAmount { amount currencyCode }',
    '        subtotalAmount { amount currencyCode }',
    '      }',
    '    }',
    '    userErrors { field message code }',
    '    warnings { code message target }',
    '  }',
    '}',
  ].join('\n');

  var GQL_GC_REMOVE = [
    'mutation DDRemoveGiftCard($cartId: ID!, $appliedGiftCardIds: [ID!]!) {',
    '  cartGiftCardCodesRemove(cartId: $cartId, appliedGiftCardIds: $appliedGiftCardIds) {',
    '    cart {',
    '      id',
    '      appliedGiftCards {',
    '        id',
    '        lastCharacters',
    '        amountUsed { amount currencyCode }',
    '        balance { amount currencyCode }',
    '      }',
    '      cost {',
    '        totalAmount { amount currencyCode }',
    '        subtotalAmount { amount currencyCode }',
    '      }',
    '    }',
    '    userErrors { field message code }',
    '  }',
    '}',
  ].join('\n');

  function ensureCartGid(opts) {
    /* Fresh cartToken from caller wins over cached page-load cartGid.
       When `dd-discount-code.js` calls apply() it just got a /cart.js
       response that includes the LIVE cart token (with the `?key=...`
       suffix Shopify returns since June 2024). The cached cartGid is
       seeded from `{{ cart.token }}` at page render and can be stale
       in two scenarios we've seen in production:
         1. The cart token rotated between page load and apply. This
            happens when the buyer's session changes (login/logout
            triggers Shopify to re-issue the cart token), or when the
            cart is destroyed + re-created (last item removed then
            new item added on some configurations).
         2. The page rendered with an empty cart (no `cart.token`),
            so `cartGid` was null on first paint, then the buyer
            added an item via AJAX. The page-render snapshot never
            updates without a navigation.
       Either way: if the caller has a fresh token, use it. Also
       reset _appliedCount when cartGid changes — that count was
       computed against whatever cart cartGid was pointing at, and is
       garbage if we just switched carts. */
    var c = cfg();
    if (opts && typeof opts.cartToken === 'string' && opts.cartToken.length) {
      var freshGid = 'gid://shopify/Cart/' + opts.cartToken;
      if (c.cartGid !== freshGid) {
        c.cartGid = freshGid;
        _appliedCount = null;
      }
      return Promise.resolve(freshGid);
    }
    if (c.cartGid && !(opts && opts.forceRefresh)) {
      return Promise.resolve(c.cartGid);
    }
    return refresh(opts);
  }

  /*
   * Cached applied-gift-card count, seeded from Liquid's
   * `cart.applied_gift_cards.size` at page render. Used to detect
   * silent-no-op rejections from cartGiftCardCodesAdd by comparing
   * `appliedGiftCards.length` after the mutation. Re-fetched only when
   * the cache is stale (cleared on configuration change) — saves one
   * Storefront request per apply.
   */
  var _appliedCount = null;

  function getAppliedCount() {
    if (_appliedCount !== null) return Promise.resolve(_appliedCount);
    var seed = cfg().appliedGiftCardsCount;
    if (typeof seed === 'number' && seed >= 0) {
      _appliedCount = seed;
      return Promise.resolve(_appliedCount);
    }
    return query()
      .then(function (c) {
        _appliedCount = c && c.appliedGiftCards ? c.appliedGiftCards.length : 0;
        return _appliedCount;
      })
      .catch(function () {
        _appliedCount = 0;
        return 0;
      });
  }

  function query(opts) {
    if (!isConfigured()) {
      return Promise.resolve(null);
    }
    return ensureCartGid(opts).then(function (gid) {
      if (!gid) return null;
      return gql(GQL_CART, { id: gid }).then(function (data) {
        if (!data || !data.cart) return null;
        return {
          appliedGiftCards: data.cart.appliedGiftCards || [],
          cost: data.cart.cost || null,
          raw: data.cart,
        };
      });
    });
  }

  function apply(code, opts) {
    if (typeof code !== 'string' || !code.trim()) {
      return Promise.resolve({ ok: false, message: 'Please enter a code.' });
    }
    if (!isConfigured()) {
      return Promise.resolve({
        ok: false,
        notReady: true,
        reason: readinessReason(),
      });
    }
    var trimmed = code.trim();
    return ensureCartGid(opts).then(function (gid) {
      if (!gid) {
        return { ok: false, notReady: true, reason: 'no cart token available (cart is empty)' };
      }
      return getAppliedCount().then(function (beforeCount) {
        return gql(GQL_GC_ADD, { cartId: gid, codes: [trimmed] }).then(function (data) {
          var result = (data && data.cartGiftCardCodesAdd) || {};
          var afterCards = (result.cart && result.cart.appliedGiftCards) || [];
          _appliedCount = afterCards.length;
          var addedCount = afterCards.length - beforeCount;
          if (addedCount <= 0) {
            /* Prefer warnings over userErrors for silent-reject
               diagnostics — the 2026-01+ API moved most non-blocking
               cart-side rejections (payments unavailable, recipient
               invalid, discount-not-found masquerading as gift card)
               into the warnings array. userErrors is reserved for
               schema-level / structural failures. */
            var warnings = (result.warnings || []);
            var firstWarn = warnings[0] || null;
            var firstUe = (result.userErrors || [])[0] || null;
            var msg = (firstWarn && firstWarn.message)
              || (firstUe && firstUe.message)
              || null;
            var code = (firstWarn && firstWarn.code)
              || (firstUe && firstUe.code)
              || null;
            return {
              ok: false,
              message: msg,
              code: code,
              warnings: warnings,
              raw: result,
            };
          }
          return {
            ok: true,
            applied: afterCards[afterCards.length - 1],
            all: afterCards,
            cost: result.cart && result.cart.cost,
            raw: result,
          };
        });
      });
    });
  }

  function remove(applicationId, opts) {
    if (!applicationId) return Promise.reject(new Error('ddSFCart.remove: applicationId required'));
    if (!isConfigured()) {
      return Promise.resolve({ ok: false, notReady: true, reason: readinessReason() });
    }
    return ensureCartGid(opts).then(function (gid) {
      if (!gid) {
        return { ok: false, notReady: true, reason: 'no cart token available' };
      }
      return gql(GQL_GC_REMOVE, {
        cartId: gid,
        appliedGiftCardIds: [applicationId],
      }).then(function (data) {
        var result = (data && data.cartGiftCardCodesRemove) || {};
        var afterCards = (result.cart && result.cart.appliedGiftCards) || [];
        _appliedCount = afterCards.length;
        if (result.userErrors && result.userErrors.length) {
          return { ok: false, message: result.userErrors[0].message, raw: result };
        }
        return {
          ok: true,
          all: afterCards,
          cost: result.cart && result.cart.cost,
          raw: result,
        };
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────
   * Client-side cart hydration
   * ────────────────────────────────────────────────────────────────
   *
   * On Shopify Plus configurations like this store's, gift cards
   * applied via the Storefront API persist to the unified cart
   * database (visible at /checkout, the SF API `cart()` query, and
   * subsequent SF mutations) but the AJAX `/cart.js` and Liquid
   * `cart.applied_gift_cards` serializers don't surface them. We
   * confirmed this empirically:
   *   • cartGiftCardCodesAdd → ok:true, cart.appliedGiftCards=[card]
   *   • SF query() → 1 applied, cost.totalAmount = post-gift-card
   *   • /cart.js → applied_gift_cards undefined, total_price unchanged
   *   • /checkout → gift card line present, total reflects deduction
   *
   * Because Liquid is blind, server-side `dd-applied-gift-cards.liquid`
   * renders nothing on first paint AND `/cart?sections=` re-renders
   * also produce empty markup. The painter (`patchDrawerFromSection`)
   * faithfully copies that empty markup over our chips. So we have
   * to drive the gift-card UI client-side from authoritative SF API
   * state.
   *
   * Hydrate runs on:
   *   • DOMContentLoaded — initial paint (in case the buyer arrived
   *     with gift cards already on the cart from a prior session)
   *   • cart:updated event — re-hydrate after every section-render
   *     that may have wiped our chips
   *   • Direct call from dd-discount-code.js after apply/remove —
   *     immediate paint without waiting for the event cascade
   *
   * Hydrate side effects (per call):
   *   • Replaces all `[data-dd-applied-gift-card]` chips inside every
   *     `[data-dd-cart-discounts]` host with chips built from SF API
   *     state (whether the server rendered any or not — SF API is
   *     authoritative).
   *   • Updates each `[data-dd-subtotal-money]` text node to the SF
   *     API `cost.totalAmount` so the displayed subtotal matches what
   *     the buyer will pay at checkout (Liquid `cart.total_price` is
   *     blind on this store, so the painter would otherwise leave it
   *     showing the pre-gift-card amount).
   *
   * Network cost: 1 Storefront query() per hydrate when the cache is
   * cold; 0 when warm (we re-use the cart payload from the latest
   * apply/remove response by passing it via `opts.cart`).
   */

  function fmtMoneyShort(amountStr, currency) {
    var n = Number(amountStr);
    if (!isFinite(n)) return '';
    /* Match Liquid's `money_without_trailing_zeros` for whole values
       and use 2-decimal precision when there are cents. Currency
       symbol prefix matches the existing chip render in the
       `dd-applied-gift-cards.liquid` snippet (which uses the
       store's `money` filter — same `$` prefix for USD here). For
       non-USD storefronts, fall back to Intl.NumberFormat with the
       returned currencyCode so we don't show a stale prefix. */
    if (currency && currency !== 'USD' && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(n);
      } catch (e) { /* fallback below */ }
    }
    if (n === Math.floor(n)) return '$' + n.toFixed(0);
    return '$' + n.toFixed(2);
  }

  function buildGiftCardChipHTML(card) {
    /* Mirrors the markup of `snippets/dd-applied-gift-cards.liquid`
       so the chips are visually indistinguishable from server-rendered
       ones. Carries `data-dd-client-rendered` so the section painter
       can identify them as ours (and we can replace cleanly). */
    var applicationId = card && card.id ? String(card.id) : '';
    var lastChars = card && card.lastCharacters ? String(card.lastCharacters) : '';
    var amountUsedRaw = card && card.amountUsed && card.amountUsed.amount;
    var currency = card && card.amountUsed && card.amountUsed.currencyCode;
    var amountFormatted = amountUsedRaw ? fmtMoneyShort(amountUsedRaw, currency) : '';
    var ariaLabel = 'Remove gift card ending in ' + lastChars;
    return [
      '<div class="dd-cart-discount dd-cart-discount--giftcard"',
      ' data-dd-applied-gift-card data-dd-client-rendered="true"',
      ' data-application-id="' + applicationId.replace(/"/g, '&quot;') + '"',
      ' data-last-characters="' + lastChars.replace(/"/g, '&quot;') + '">',
      '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
      '<rect x="3" y="8" width="18" height="13" rx="2"/>',
      '<path d="M3 12h18"/><path d="M12 8v13"/>',
      '<path d="M7.5 8a2.5 2.5 0 1 1 0-5C9 3 12 8 12 8s3-5 4.5-5a2.5 2.5 0 1 1 0 5"/>',
      '</svg>',
      '<span>Gift card &middot;&middot;&middot;&middot;' + lastChars + (amountFormatted ? ' (-' + amountFormatted + ')' : '') + '</span>',
      '<button type="button" class="dd-cart-discount__remove" data-dd-applied-gift-card-remove aria-label="' + ariaLabel.replace(/"/g, '&quot;') + '">',
      '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">',
      '<path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
      '</svg></button></div>',
    ].join('');
  }

  function paintCartFromState(state) {
    /* `state` shape: { appliedGiftCards: [...], cost: {totalAmount, subtotalAmount} }
       Tolerant of partials — null/empty appliedGiftCards just means
       "remove any chips on the page", null cost means "leave totals
       alone". */
    if (!state) return;
    /* Accept both shapes: query() returns `{appliedGiftCards, cost}`,
       apply()/remove() return `{all, cost}`. Same data, two names —
       absorb both so callers don't have to reshape. */
    var cards = state.appliedGiftCards || state.all || [];

    var hosts = document.querySelectorAll('[data-dd-cart-discounts]');
    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      /* Remove any existing gift-card chips (server-rendered or
         client-rendered from a prior hydrate). SF API state is
         authoritative — we replace wholesale rather than diff. */
      var stale = host.querySelectorAll('[data-dd-applied-gift-card]');
      for (var s = 0; s < stale.length; s++) stale[s].parentNode.removeChild(stale[s]);
      /* Insert fresh chips. Cards are appended at the end of the
         host so the layout follows: discount badge(s) → gift card
         chip(s) → note badge. This matches the snippet's natural
         render order on stores where Liquid does see the cards. */
      for (var c = 0; c < cards.length; c++) {
        var html = buildGiftCardChipHTML(cards[c]);
        var noteBadge = host.querySelector('[data-dd-note-badge]');
        if (noteBadge) {
          noteBadge.insertAdjacentHTML('beforebegin', html);
        } else {
          host.insertAdjacentHTML('beforeend', html);
        }
      }
    }

    /* Update subtotal display(s). On stores where Liquid is blind to
       SF gift cards, the section painter will set the subtotal back
       to pre-gift-card on every refresh — we re-set it here on each
       hydrate. The `data-dd-sf-cart-total` attribute records what
       value we last wrote so we can detect the painter's writes
       (different value) and re-correct. */
    var totalAmount = state.cost && state.cost.totalAmount && state.cost.totalAmount.amount;
    var totalCurrency = state.cost && state.cost.totalAmount && state.cost.totalAmount.currencyCode;
    if (totalAmount != null && cards.length > 0) {
      var n = Number(totalAmount);
      if (isFinite(n)) {
        var formatted = fmtMoneyShort(totalAmount, totalCurrency);
        /* For the subtotal we want trailing-zero behavior matching
           Liquid `money_with_currency` (e.g. "$495.00 USD"). The
           short formatter strips trailing zeros for chip clarity,
           but the subtotal slot is wider and the surrounding markup
           expects the verbose form. Use 2-decimal precision and
           append currency code when not USD. */
        var subtotalText = '$' + n.toFixed(2);
        if (totalCurrency && totalCurrency !== 'USD') subtotalText += ' ' + totalCurrency;
        else subtotalText += ' USD';
        var moneyEls = document.querySelectorAll('[data-dd-subtotal-money]');
        for (var m = 0; m < moneyEls.length; m++) {
          moneyEls[m].textContent = subtotalText;
          moneyEls[m].setAttribute('data-dd-sf-cart-total', String(n));
        }
      }
    }
  }

  /* In-flight guard so concurrent hydrate() calls don't double-fetch.
     The promise is shared until it resolves; subsequent callers piggy-
     back on it. */
  var _hydratePromise = null;

  /* Last-known authoritative cart state from SF API. Captured after
     every successful query/apply/remove. Used as the "instant paint"
     source on drawer:opened so the chip + subtotal show up at the
     same moment the drawer becomes visible — no flash of wrong state
     while we wait for a fresh network round-trip. The trailing
     network query then validates and corrects (rare). */
  var _lastState = null;

  function captureState(state) {
    if (state && (state.appliedGiftCards || state.all || state.cost)) {
      _lastState = {
        appliedGiftCards: state.appliedGiftCards || state.all || [],
        cost: state.cost || null,
      };
    }
  }

  function hydrate(opts) {
    if (!isConfigured()) return Promise.resolve();
    /* Fast path: caller already has cart state (e.g. from a recent
       apply/remove response). Skip the network round-trip. */
    if (opts && opts.state) {
      captureState(opts.state);
      paintCartFromState(opts.state);
      return Promise.resolve();
    }
    /* Instant paint from cache: if we have a known state from a
       recent operation, paint it now. The network refresh below
       still runs in case the cache is stale (e.g. another tab
       changed the cart). The two-stage paint is harmless because
       it's idempotent and authoritative-network-state always
       overrides. */
    if (_lastState) {
      paintCartFromState(_lastState);
    }
    if (_hydratePromise) return _hydratePromise;
    _hydratePromise = ensureCartGid(opts)
      .then(function (gid) {
        if (!gid) return null;
        return query(opts);
      })
      .then(function (state) {
        captureState(state);
        paintCartFromState(state);
      })
      .catch(function (err) {
        console.warn('[ddSFCart.hydrate] error', err);
      })
      .then(function () {
        _hydratePromise = null;
      });
    return _hydratePromise;
  }

  /* Auto-init: hydrate once on DOMContentLoaded (or immediately if the
     DOM is already past that point), and re-hydrate on every
     cart:updated event. The painter inside `cart-paint.js` runs
     synchronously before `cart:updated` is dispatched, so by the time
     we hydrate, any chips it wiped are still gone — our re-hydrate is
     what restores them.

     The 50ms debounce coalesces rapid bursts (e.g. a quantity change
     dispatches cart:updated then a cascading shipping-protection
     update dispatches it again ~30ms later). */
  var _hydrateDebounceTimer = null;
  function scheduleHydrate() {
    if (_hydrateDebounceTimer) clearTimeout(_hydrateDebounceTimer);
    _hydrateDebounceTimer = setTimeout(function () {
      _hydrateDebounceTimer = null;
      hydrate();
    }, 50);
  }

  function initHydrator() {
    if (!isConfigured()) return;
    /* Don't run on pages that don't have the discount strip. Saves
       one Storefront query() per pageview on product/collection/etc.
       Note: cart-drawer markup IS present on every page (the drawer
       lives in layout/theme.liquid), so this check passes on PDP /
       collection / search / etc. — which is what we want, because
       opening the drawer from any page must restore the chip. */
    if (!document.querySelector('[data-dd-cart-discounts]')) return;
    hydrate();

    /* Listen on every event the codebase uses to signal a cart UI
       change. We need all three because they don't always fire
       together:
         • cart:updated — the DOM event published by `cart-notify.js`
           after Shopify cart mutations (/cart/update, /cart/change,
           /cart/add, /cart/clear). Catches every real mutation.
         • ddx:cart-mutated — the older alias still emitted by some
           paths (see docs/contracts/events-map.json). Some paths
           emit ddx without cart:updated, so listening to both is
           defensive.
         • drawer:opened — fires on every drawer open. The
           cart-drawer markup is repainted from cached server HTML
           when the drawer opens (no mutation, no cart:updated),
           and that repaint wipes our client-rendered chip. We
           hydrate immediately on open to restore the chip with no
           visible gap. */
    document.addEventListener('cart:updated', scheduleHydrate);
    document.addEventListener('ddx:cart-mutated', scheduleHydrate);
    document.addEventListener('drawer:opened', scheduleHydrate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHydrator);
  } else {
    initHydrator();
  }

  window.ddSFCart = {
    apply: apply,
    remove: remove,
    query: query,
    refresh: refresh,
    hydrate: hydrate,
    isReady: isReady,
    isConfigured: isConfigured,
    hasCart: hasCart,
    readinessReason: readinessReason,
  };
})();
